<template>
  <b-layout :colors="$site.colors" :header-title="servicesTitle" :header-back-route="homeRoute">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <b-card :title="$t('pages.book.index.selection')" :class="(moveReservation || isBotLayout || isWidgetLayout) ? 'pt-6 block' : 'hidden sm:block'">
            <move-detail />

            <div v-if="selectedServices.length > 0">
              <div>
                <template v-for="(serviceSelection, index) in selectedServices" :key="`slotService-`+ index">
                  <div class="flex items-start justify-between text-sm text-secondary mb-2">
                    <div>
                      <p class="font-semibold">
                        {{ serviceSelection.service.title }}
                      </p>
                      <p>
                        <template v-if="serviceSelection.selectedEmployee">
                          {{ $t('general.withEmployee',{employee: serviceSelection.selectedEmployee.firstName}) }}
                        </template>
                        <template v-else>
                          {{ $t('general.withAny') }}
                        </template>
                      </p>
                    </div>

                    <div>
                      <p v-if="$site.settings.bookShowServicePrices && priceLabelForServiceSelection(serviceSelection)">
                        {{ priceLabelForServiceSelection(serviceSelection) }}
                      </p>
                    </div>
                  </div>
                </template>
              </div>

              <p class="text-secondary text-sm mt-12 opacity-80">
                <span v-if="(selectedServices[0].service.customerType == 1 || !$site.settings.allowMultiService)">
                  {{ $t('pages.book.index.nextToSeeAvailability') }}
                </span>
                <span v-else>
                  {{ $t('pages.book.index.bookMultipleServices') }}
                </span>
              </p>
            </div>

            <p v-else class="text-secondary text-sm">
              <span v-if="$site.settings.allowMultiService">
                {{ $t('pages.book.index.selectAtLeastOne') }}
              </span>
              <span v-else>
                {{ $t('pages.book.index.selectAService') }}
              </span>
            </p>

            <template #action>
              <div
                class="w-full relative z-30 px-0 mt-12 py-0 bg-transparent"
                :class="[
                  isNextDisabled ? 'hidden sm:block' : ''
                ]"
              >
                <b-button
                  :name="$t('general.next')"
                  class="w-full"
                  size="lg"
                  variant="action"
                  :disabled="isNextDisabled"
                  @click="next($event)"
                >
                  {{ $t('general.next') }}
                </b-button>
              </div>
            </template>
          </b-card>

          <ClientOnly>
            <Teleport to="#teleports">
              <div
                v-if="!isBotLayout && !isWidgetLayout && selectedServices.length > 0 && !isNextDisabled"
                class="block sm:hidden w-full px-4 sm:px-0 bg-primary/70 backdrop-blur border-t py-4 border-b sm:border-none sm:py-0 sm:bg-transparent"
              >
                <div class="text-sm flex items-center justify-between mb-3 pb-3 border-b">
                  <p class="text-secondary/80">{{ selectedServices.length }} service{{ selectedServices.length > 1 ? 's' : '' }} selected</p>
                  <p class="text-secondary/80 font-medium">{{ priceLabel }}</p>
                </div>
                <b-button
                  :name="$t('general.next')"
                  class="w-full"
                  variant="action"
                  :disabled="isNextDisabled"
                  @click="next($event)"
                >
                  {{ $t('general.next') }}
                </b-button>
              </div>
            </Teleport>
          </ClientOnly>
        </template>
        <div>
          <voucher-alert />
          <pass-alert />

          <services-list
            :service-filter="serviceListFilter"
            :employee-filter="serviceListEmployeeFilter"
            :show-any-employee="$site.settings.bookShowFirstAvailable"
            :force-select-employee="$site.settings.allowSelectEmployee == 2"
            :show-employee-selector="$site.settings.allowSelectEmployee > 0"
            :allow-multiple-selections="$site.settings.allowMultiService"
            :next-action="next"
            :next-title="$t('general.next')"
            @change="updateUrl"
          />
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import ServicesList from '@/components/booking/elements/Services.vue'
import VoucherAlert from '@/components/voucher/elements/Alert.vue'
import PassAlert from '@/components/pass/elements/Alert.vue'
import MoveDetail from '@/components/booking/elements/MoveDetail.vue'
import { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'
import { BookingElementsAddOnsModal } from '#components'

export default defineNuxtComponent({
  name: 'ReservationHome',
  meta: {
    tabs: ['online'],
    middleware: ['redirects']
  },
  layout: 'default',
  components: {
    ServicesList,
    VoucherAlert,
    PassAlert,
    MoveDetail
  },
  filters: {
    employeeName (employee) {
      if (!employee) { return 'Any' }
      return employee.firstName
    }
  },
  data () {
    return {
      serviceFilter: null,
      moveReservation: null,
    }
  },
  async asyncData () {
    useTabMeta('book')

    const { $api } = useNuxtApp()
    const route = useRoute()
    const locationStore = useLocationStore()
    const bookingStore = useBookingStore()

    try {
      await locationStore.getServices()
      await bookingStore.setFromUrlQuery(route.query)

      if (bookingStore.pass) {
        const pass = bookingStore.pass

        try {
          const response = await $api.pass.customerPass(pass.id, pass.signature)

          return {
            pass: response.customerPass
          }
        } catch (error) {
          console.error(error)
          return {
            pass: null
          }
        }
      }
    } catch (e) {
      // console.log(e)
    }

    return {}
  },
  setup () {
    const modal = useModal()

    return {
      modal
    }
  },
  computed: {
    title () {
      return this.$site.tabs.getName('book', 'Book')
    },
    info () {
      return this.$site.tabs.getInfo('book')
    },
    isBotLayout () {
      return this.$store.getters.layout === 'bot'
    },
    isWidgetLayout () {
      return this.$store.getters.layout === 'widget'
    },
    isNextDisabled () {
      return this.selectedServices.length === 0
        || (!this.$site.settings.bookShowFirstAvailable && !this.servicesSelectedEmployee && this.$site.settings.allowSelectEmployee > 0)
    },
    hasServiceAssociations () {
      return this.selectedServices.some(service => service.service.associations?.length > 0)
    },
    hasAvailableAddOns () {
      const selectedServiceIds = this.selectedServices.reduce((acc, service) => {
        if (service.selected) {
          acc.push(service.service.id)
        }

        return acc
      }, [])

      for (const { service } of this.selectedServices) {
        if (!service.associations) { continue }

        for (const association of service.associations) {
          // Skip is association is not available online
          if (!association.target?.allowInternet) { continue }

          // Check if target is not already selected
          if (!selectedServiceIds.includes(association.target.id)) {
            return true
          }
        }
      }

      return false
    },
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    servicesTitle () {
      if (this.filters.category) {
        const services = this.services
        for (let i = 0; i < services.categories.length; i++) {
          const category = services.categories[i]
          if (category.id === this.filters.category) {
            return category.name
          }
        }
      }
      return this.$t('pages.book.index.services')
    },
    selectedServices () {
      return this.$store.getters['booking/selectedServices']
    },
    services () {
      return this.$store.getters['location/services']
    },
    filters () {
      return this.$store.getters['booking/filters']
    },
    voucher () {
      return this.$store.getters['booking/voucher']
    },
    servicesSelectedEmployee () {
      const selectedServices = this.selectedServices

      for (const serviceSelection of selectedServices) {
        if (!serviceSelection.selectedEmployee) {
          const service = serviceSelection.service
          const employeeServices = []

          for (let i = 0; i < service.employeeServices.length; i++) {
            const employeeService = service.employeeServices[i]
            const isEmployee = employeeService.employee && employeeService.employee.type === 0
            if (!isEmployee || (this.employeeFilter && !this.employeeFilter(service, employeeService.employee))) {
              continue
            }

            employeeServices.push(employeeService)
          }

          if (employeeServices.length !== 0) {
            return false
          }
        }
      }

      return true
    },
    priceLabel () {
      const currency = this.$site.location.currencySymbol
      const selectedServices = this.selectedServices
      let total = 0
      let from = false

      for (const serviceSelection of selectedServices) {
        if (serviceSelection.selectedEmployee && serviceSelection.service && serviceSelection.service.employeeServices) {
          for (let i = 0; i < serviceSelection.service.employeeServices.length; ++i) {
            if (serviceSelection.service.employeeServices[i].employee.id === serviceSelection.selectedEmployee.id) {
              total += serviceSelection.service.employeeServices[i].price
              if (serviceSelection.service.employeeServices[i].priceFrom) {
                from = true
              }
            }
          }
        } else {
          total += serviceSelection.service.price
          if (serviceSelection.service.priceFrom) {
            from = true
          }
        }
      }

      return (from ? 'from ' : '') + currency + total
    }
  },
  watch: {
    $route () {
      // this.$store.dispatch('booking/setFromUrlQuery', this.$route.query)
    }
  },
  mounted () {
    this.moveReservation = this.$store.getters['booking/moveReservation']
  },
  methods: {
    serviceListFilter (service, category) {
      if (this.filters.service) {
        const services = this.filters.service.split(',').map(x => parseInt(x))

        if (services.includes(service.id)) {
          return true
        }

        return false
      }

      if (this.filters.category) {
        const categories = this.filters.category.split(',').map(x => parseInt(x))

        if (categories.includes(0)) {
          if (!category.id) {
            return true
          }
        }

        if (categories.includes(category.id)) {
          return true
        }

        return false
      }

      if (this.voucher && this.voucher.isValid && this.voucher.validity && this.voucher.validity.services) {
        return this.voucher.validity.services.find(validService => validService.id === service.id)
      }

      if (this.pass && this.pass.pass && this.pass.pass.services) {
        return this.pass.pass.services.find(validService => validService.idService === service.id)
      }

      return true
    },
    serviceListEmployeeFilter (service, employee) {
      if (this.voucher && this.voucher.isValid && this.voucher.validity && this.voucher.validity.employees) {
        return this.voucher.validity.employees.find(validEmployee => validEmployee.id === employee.id)
      }

      return true
    },
    next (event) {
      if (this.hasAvailableAddOns) {
        const that = this

        // Get trigger element position for modal anchoring (helps with nested iframes like Wix)
        let anchorTop = 0
        if (event?.target) {
          const rect = event.target.getBoundingClientRect()
          // Use the element's top position relative to the document
          anchorTop = rect.top + window.scrollY
        }

        this.modal.open(BookingElementsAddOnsModal, {
          anchorTop,
          onSuccess() {
            that.navigateToAvailability()

            that.modal.close()
          }
        })

        return
      }

      this.navigateToAvailability()
    },
    async navigateToAvailability () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      navigateTo(createBookingRoute(bookingUrlQuery, BookingStage.AVAILABILITY))
    },
    updateUrl () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']
      const route = createBookingRoute(bookingUrlQuery, BookingStage.SERVICES)

      this.$router.push({ query: route.query })
    },
    removeService (service) {
      this.$store.dispatch('booking/setServiceSelected', { service, selected: false })
      this.updateUrl()
    },
    priceLabelForServiceSelection (serviceSelection) {
      // Check if this service is part of an association with a discount
      const hasDiscount = serviceSelection.association && serviceSelection.association.discountAmount > 0

      if (hasDiscount) {
        const association = serviceSelection.association
        const discountAmount = association.discountAmount
        const discountType = association.discountType
        const symbol = this.$site.location.currencySymbol

        let price = 0
        let priceFrom = false

        // Determine base price
        if (serviceSelection.selectedEmployee && serviceSelection.service && serviceSelection.service.employeeServices) {
          for (let i = 0; i < serviceSelection.service.employeeServices.length; ++i) {
            if (serviceSelection.service.employeeServices[i].employee.id === serviceSelection.selectedEmployee.id) {
              price = serviceSelection.service.employeeServices[i].price
              priceFrom = serviceSelection.service.employeeServices[i].priceFrom
              break
            }
          }
        } else {
          price = serviceSelection.service.price
          priceFrom = serviceSelection.service.priceFrom
        }

        // Calculate discounted price
        let discountedAmount = 0
        if (discountType === 0) {
          // Fixed amount discount
          discountedAmount = price - discountAmount
        } else if (discountType === 1) {
          // Percentage discount
          discountedAmount = price - (price * discountAmount / 100)
        }

        return (priceFrom ? 'from ' : '') + symbol + parseFloat(discountedAmount || 0).toFixed(2)
      }

      // No discount - return normal price
      if (serviceSelection.selectedEmployee && serviceSelection.service && serviceSelection.service.employeeServices) {
        for (let i = 0; i < serviceSelection.service.employeeServices.length; ++i) {
          if (serviceSelection.service.employeeServices[i].employee.id === serviceSelection.selectedEmployee.id) {
            return (serviceSelection.service.employeeServices[i].priceFrom ? 'from ' : '') + serviceSelection.service.employeeServices[i].priceLabel
          }
        }
        return ''
      } else {
        return (serviceSelection.service.priceFrom ? 'from ' : '') + serviceSelection.service.priceLabel
      }
    }
  }
})
</script>

<style scoped>
.confirmation-details--services--service
{
  font-weight: bold;
  font-size: 16px;
}

.confirmation-details--services--employee
{
  color: var(--bookapp-content-text-color) !important;
  font-size: 13px;
}
.confirmation-details--services--price
{
  font-size: 14px;
}
</style>
